import React, { useCallback, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Breadcrumbs,
    Button,
    Chip,
    IconButton,
    Link,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft,
    faCircleNodes,
    faPenToSquare,
    faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import { ArticleView, RequirementView } from './calculatorApi';

/** Womit ein Knoten im Graphen zu tun hat — bestimmt Farbe und Beschriftung. */
type NodeKind = 'focus' | 'parent' | 'option' | 'article' | 'resource';

interface GraphNode {
    key: string;
    kind: NodeKind;
    /** Anforderungs-Id bzw. Artikelnummer — Grundlage der Navigation. */
    ref: string;
    title: string;
    subtitle?: string;
    /** Kennzeichnungen wie „Pflicht", „exklusiv", „vorbelegt". */
    badges: string[];
    warn?: string;
    x: number;
    y: number;
    w: number;
    h: number;
}

interface GraphEdge {
    key: string;
    from: GraphNode;
    to: GraphNode;
    label?: string;
    dashed?: boolean;
}

/** Eine Optionsgruppe mit ihren aufgelösten Optionen — Zwischenergebnis des Layouts. */
interface ResolvedGroup {
    title: string;
    exclusive: boolean;
    mandatory: boolean;
    options: { id: string; title: string; isDefault: boolean; anzahl: number | null; inOffer: boolean }[];
}

const NODE_W = 250;
const NODE_H = 62;
const GAP_Y = 14;
const GAP_X = 110;
const GROUP_HEADER_H = 26;

/**
 * Der Anforderungsgraph — eine Fokusansicht statt eines Gesamtnetzes.
 *
 * **Warum Fokus und nicht alles auf einmal.** Achtundachtzig Anforderungen mit ihren Artikeln und
 * Optionen ergeben ein Knäuel, das hübsch aussieht und nichts beantwortet. Gezeigt wird deshalb
 * immer genau eine Anforderung mit ihrer unmittelbaren Nachbarschaft; ein Klick auf einen Nachbarn
 * macht diesen zum neuen Mittelpunkt. So entsteht Navigation statt Übersichtsillusion.
 *
 * **Warum zwei Ebenen.** Oben steht, was fachlich zusammenhängt: wer diese Anforderung als Option
 * anbietet, und welche Optionen sie selbst zur Wahl stellt. Unten steht, woraus sie besteht —
 * Ressourcen und Artikel. Das sind zwei verschiedene Fragen, und sie in einer Ebene zu mischen war
 * der Grund, warum die alte Liste unlesbar wurde.
 *
 * **Warum ohne Graph-Bibliothek.** Die Anordnung ist Spalten, keine Physik: die Positionen stehen
 * vor dem Rendern fest, nichts wackelt, nichts überlappt, und die Farben kommen aus dem Theme.
 */
export const RequirementGraph: React.FC<{
    requirements: RequirementView[];
    articles: ArticleView[];
    /** Startpunkt, etwa aus der Tabellenauswahl. */
    initialId?: string | null;
    /** Öffnet den normalen Bearbeiten-Dialog für diese Anforderung. */
    onEdit?: (requirement: RequirementView) => void;
}> = ({ requirements, articles, initialId, onEdit }) => {
    const theme = useTheme();
    const [history, setHistory] = useState<string[]>(initialId ? [initialId] : []);

    const byId = useMemo(() => new Map(requirements.map((r) => [r.id, r])), [requirements]);
    const articleByNr = useMemo(() => new Map(articles.map((a) => [a.articleNumber, a])), [articles]);

    /**
     * Wer bietet diese Anforderung als Option an? Steht nirgends in den Daten und ist trotzdem die
     * häufigste Frage der Pflege: „Wenn ich das hier ändere — wo schlägt das durch?"
     */
    const parentsOf = useMemo(() => {
        const map = new Map<string, { id: string; title: string; group: string }[]>();
        for (const r of requirements) {
            for (const g of r.optionGroups ?? []) {
                for (const o of g.options ?? []) {
                    if (!o.anforderungId) continue;
                    const list = map.get(o.anforderungId) ?? [];
                    list.push({ id: r.id, title: r.titel || r.id, group: g.title || 'Optionen' });
                    map.set(o.anforderungId, list);
                }
            }
        }
        return map;
    }, [requirements]);

    const focusId = history[history.length - 1] ?? null;
    const focus = focusId ? byId.get(focusId) ?? null : null;

    const navigate = useCallback((id: string) => {
        setHistory((h) => (h[h.length - 1] === id ? h : [...h, id]));
    }, []);

    const back = useCallback(() => setHistory((h) => (h.length > 1 ? h.slice(0, -1) : h)), []);

    /**
     * Einmal tippen wählt aus, zweimal springt weiter.
     *
     * Auf einem Tablet gibt es kein Überfahren, und ein Sprung beim ersten Antippen führt
     * regelmäßig dorthin, wo man gar nicht hinwollte — man wollte nur nachsehen, was da steht.
     */
    const [selected, setSelected] = useState<string | null>(null);

    const tap = useCallback((id: string) => {
        setSelected((cur) => {
            if (cur === id) {
                navigate(id);
                return null;
            }
            return id;
        });
    }, [navigate]);

    const layout = useMemo(() => {
        if (!focus) return null;
        return buildLayout(focus, parentsOf.get(focus.id) ?? [], byId, articleByNr);
    }, [focus, parentsOf, byId, articleByNr]);

    const color = (kind: NodeKind) => {
        switch (kind) {
            case 'focus': return theme.palette.primary.main;
            case 'parent': return theme.palette.secondary.main;
            case 'option': return theme.palette.info.main;
            case 'article': return theme.palette.text.secondary;
            case 'resource': return theme.palette.warning.main;
        }
    };

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Autocomplete
                    size="small"
                    sx={{ minWidth: 380, flexGrow: 1, maxWidth: 620 }}
                    options={requirements}
                    value={focus}
                    getOptionLabel={(r) => `${r.id} — ${r.titel || ''}`}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    onChange={(_, r) => r && navigate(r.id)}
                    renderInput={(params) => (
                        <TextField {...params} label="Anforderung" placeholder="Id oder Titel …" />
                    )}
                    renderOption={(props, r) => (
                        <li {...props} key={r.id}>
                            <Stack>
                                <Typography variant="body2">{r.titel || r.id}</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                                    {r.id} · {r.category}
                                </Typography>
                            </Stack>
                        </li>
                    )}
                />

                {history.length > 1 && (
                    <Tooltip title="Zurück">
                        <IconButton onClick={back} size="small">
                            <FontAwesomeIcon icon={faArrowLeft} />
                        </IconButton>
                    </Tooltip>
                )}

                {focus && onEdit && (
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FontAwesomeIcon icon={faPenToSquare} />}
                        onClick={() => onEdit(focus)}
                    >
                        Bearbeiten
                    </Button>
                )}
            </Stack>

            {history.length > 1 && (
                <Breadcrumbs maxItems={5} sx={{ fontSize: 13 }}>
                    {history.map((id, i) => (
                        <Link
                            key={`${id}-${i}`}
                            component="button"
                            underline={i === history.length - 1 ? 'none' : 'hover'}
                            color={i === history.length - 1 ? 'text.primary' : 'inherit'}
                            onClick={() => setHistory(history.slice(0, i + 1))}
                            sx={{ fontFamily: 'monospace', fontSize: 12 }}
                        >
                            {id}
                        </Link>
                    ))}
                </Breadcrumbs>
            )}

            {!focus && (
                <Alert severity="info" icon={<FontAwesomeIcon icon={faCircleNodes} />}>
                    Wähle eine Anforderung, um ihre Zusammenhänge zu sehen — wer sie als Option
                    anbietet, welche Optionen sie selbst zur Wahl stellt und woraus sie besteht.
                </Alert>
            )}

            {focus && layout && (
                <Paper
                    variant="outlined"
                    sx={{
                        position: 'relative',
                        overflow: 'auto',
                        p: 2,
                        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.03 : 0.02),
                    }}
                >
                    <Box sx={{ position: 'relative', width: layout.width, height: layout.height, minWidth: '100%' }}>
                        {/* Kanten liegen hinter den Knoten — sonst zerschneiden sie die Beschriftung. */}
                        <Box
                            component="svg"
                            width={layout.width}
                            height={layout.height}
                            sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}
                        >
                            {layout.edges.map((e) => (
                                <Edge key={e.key} edge={e} />
                            ))}
                        </Box>

                        {layout.groupLabels.map((g) => (
                            <Box
                                key={g.key}
                                sx={{
                                    position: 'absolute', left: g.x, top: g.y, width: NODE_W,
                                    display: 'flex', alignItems: 'center', gap: 0.5,
                                }}
                            >
                                <Typography variant="caption" noWrap sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                    {g.title}
                                </Typography>
                                {g.mandatory && <Chip size="small" color="warning" label="Pflicht" sx={{ height: 18, fontSize: 10 }} />}
                                {g.exclusive && <Chip size="small" variant="outlined" label="nur eine" sx={{ height: 18, fontSize: 10 }} />}
                            </Box>
                        ))}

                        {layout.nodes.map((n) => (
                            <Node
                                key={n.key}
                                node={n}
                                accent={color(n.kind)}
                                selected={selected === n.ref && n.kind !== 'focus'}
                                onClick={n.kind === 'article' || n.kind === 'resource' ? undefined : () => tap(n.ref)}
                            />
                        ))}
                    </Box>
                </Paper>
            )}

            {focus && layout && layout.nodes.length === 1 && (
                <Alert severity="warning" variant="outlined">
                    Diese Anforderung steht für sich allein: keine Optionen, keine Artikel, keine
                    Ressourcen und niemand bietet sie an. Bei einer nicht-impliziten Anforderung ist
                    das meist ein Pflegefehler.
                </Alert>
            )}
        </Stack>
    );
};

/** Ein Knoten. Anforderungen sind anklickbar, Artikel und Ressourcen sind Endpunkte. */
const Node: React.FC<{
    node: GraphNode;
    accent: string;
    /** Einmal getippt: hervorgehoben, aber noch nicht gesprungen. */
    selected?: boolean;
    onClick?: () => void;
}> = ({ node, accent, selected, onClick }) => {
    const theme = useTheme();
    const isFocus = node.kind === 'focus';

    return (
        <Paper
            elevation={isFocus ? 3 : 0}
            onClick={onClick}
            sx={{
                position: 'absolute',
                left: node.x, top: node.y, width: node.w, height: node.h,
                px: 1.25, py: 0.75,
                display: 'flex', flexDirection: 'column', justifyContent: 'center',
                borderLeft: `3px solid ${accent}`,
                borderRadius: 1,
                cursor: onClick ? 'pointer' : 'default',
                bgcolor: isFocus || selected ? alpha(accent, 0.1) : 'background.paper',
                border: isFocus ? undefined : `1px solid ${theme.palette.divider}`,
                borderLeftWidth: 3,
                borderLeftColor: accent,
                outline: selected ? `1px solid ${accent}` : undefined,
                transition: theme.transitions.create(['box-shadow', 'background-color']),
                '&:hover': onClick ? { boxShadow: theme.shadows[4], bgcolor: alpha(accent, 0.06) } : undefined,
            }}
        >
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                <Typography
                    variant="caption"
                    noWrap
                    sx={{ color: accent, fontWeight: 600, fontFamily: 'monospace' }}
                >
                    {node.ref}
                </Typography>
                {selected && (
                    <Typography variant="caption" sx={{ color: accent, fontWeight: 600 }}>
                        ↵ nochmal tippen
                    </Typography>
                )}
                {node.warn && (
                    <Tooltip title={node.warn}>
                        <Box component="span" sx={{ color: 'warning.main', display: 'flex' }}>
                            <FontAwesomeIcon icon={faTriangleExclamation} fontSize={11} />
                        </Box>
                    </Tooltip>
                )}
            </Stack>

            <Tooltip title={node.title} enterDelay={600}>
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: isFocus ? 600 : 400, lineHeight: 1.25,
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}
                >
                    {node.title}
                </Typography>
            </Tooltip>

            {node.badges.length > 0 && (
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.25 }}>
                    {node.badges.map((b) => (
                        <Chip key={b} size="small" label={b} sx={{ height: 15, fontSize: 9.5 }} />
                    ))}
                </Stack>
            )}
        </Paper>
    );
};

/** Eine Kante als Bézier-Kurve — von der rechten Kante des einen zur linken des anderen. */
const Edge: React.FC<{ edge: GraphEdge }> = ({ edge }) => {
    const theme = useTheme();
    const { from, to } = edge;
    const vertical = to.y > from.y + from.h;

    const x1 = vertical ? from.x + from.w / 2 : from.x + from.w;
    const y1 = vertical ? from.y + from.h : from.y + from.h / 2;
    const x2 = vertical ? to.x + to.w / 2 : to.x;
    const y2 = vertical ? to.y : to.y + to.h / 2;

    const d = vertical
        ? `M ${x1} ${y1} C ${x1} ${(y1 + y2) / 2}, ${x2} ${(y1 + y2) / 2}, ${x2} ${y2}`
        : `M ${x1} ${y1} C ${x1 + GAP_X / 2} ${y1}, ${x2 - GAP_X / 2} ${y2}, ${x2} ${y2}`;

    return (
        <path
            d={d}
            fill="none"
            stroke={theme.palette.divider}
            strokeWidth={1.5}
            strokeDasharray={edge.dashed ? '4 4' : undefined}
        />
    );
};

/**
 * Berechnet die Anordnung.
 *
 * Bewusst analytisch statt gemessen: die Spaltenbreiten stehen fest, also stehen auch alle
 * Positionen fest, bevor irgendetwas gerendert wird. Das erspart einen zweiten Renderdurchlauf und
 * lässt die Kanten exakt an den Kanten der Kästen enden.
 */
function buildLayout(
    focus: RequirementView,
    parents: { id: string; title: string; group: string }[],
    byId: Map<string, RequirementView>,
    articleByNr: Map<string, ArticleView>,
) {
    const nodes: GraphNode[] = [];
    const edges: GraphEdge[] = [];
    const groupLabels: { key: string; title: string; x: number; y: number; mandatory: boolean; exclusive: boolean }[] = [];

    const groups: ResolvedGroup[] = (focus.optionGroups ?? []).map((g, i) => ({
        title: g.title || `Optionen ${i + 1}`,
        exclusive: !!g.exclusive,
        mandatory: !!g.mandatory,
        options: (g.options ?? []).map((o) => {
            const target = o.anforderungId ? byId.get(o.anforderungId) : undefined;
            return {
                id: o.anforderungId || '?',
                title: target?.titel || o.anforderungId || '?',
                isDefault: !!o.default,
                anzahl: o.anzahl ?? null,
                inOffer: o.imAngebot !== false,
            };
        }),
    }));

    // Höhe der rechten Spalte: je Gruppe eine Überschrift plus ihre Optionen.
    const rightHeight = groups.reduce(
        (sum, g) => sum + GROUP_HEADER_H + g.options.length * (NODE_H + GAP_Y) + GAP_Y,
        0,
    );
    const leftHeight = parents.length * (NODE_H + GAP_Y);
    const topHeight = Math.max(rightHeight, leftHeight, NODE_H);

    const colLeft = 0;
    const colCenter = NODE_W + GAP_X;
    const colRight = 2 * (NODE_W + GAP_X);

    // Der Fokus sitzt mittig zur höchsten Spalte, damit die Kanten symmetrisch laufen.
    const focusNode: GraphNode = {
        key: 'focus', kind: 'focus', ref: focus.id,
        title: focus.titel || focus.id,
        badges: [
            ...(focus.implicit ? ['implizit'] : []),
            ...(focus.eingabe ? [focus.eingabe] : []),
        ],
        x: colCenter, y: (topHeight - NODE_H) / 2, w: NODE_W, h: NODE_H,
    };
    nodes.push(focusNode);

    parents.forEach((p, i) => {
        const node: GraphNode = {
            key: `p-${p.id}-${i}`, kind: 'parent', ref: p.id, title: p.title,
            badges: [p.group],
            x: colLeft, y: (topHeight - leftHeight) / 2 + i * (NODE_H + GAP_Y), w: NODE_W, h: NODE_H,
        };
        nodes.push(node);
        edges.push({ key: `e-${node.key}`, from: node, to: focusNode });
    });

    let y = (topHeight - rightHeight) / 2;
    groups.forEach((g, gi) => {
        groupLabels.push({
            key: `g-${gi}`, title: g.title, x: colRight, y,
            mandatory: g.mandatory, exclusive: g.exclusive,
        });
        y += GROUP_HEADER_H;

        g.options.forEach((o, oi) => {
            const missing = !byId.has(o.id);
            const node: GraphNode = {
                key: `o-${gi}-${o.id}-${oi}`, kind: 'option', ref: o.id, title: o.title,
                badges: [
                    ...(o.isDefault ? ['vorbelegt'] : []),
                    ...(o.anzahl ? [`×${o.anzahl}`] : []),
                    ...(o.inOffer ? [] : ['nicht im Angebot']),
                ],
                warn: missing ? 'Diese Anforderung existiert nicht — toter Verweis.' : undefined,
                x: colRight, y, w: NODE_W, h: NODE_H,
            };
            nodes.push(node);
            edges.push({ key: `e-${node.key}`, from: focusNode, to: node, dashed: !o.inOffer });
            y += NODE_H + GAP_Y;
        });

        y += GAP_Y;
    });

    // Untere Ebene: woraus die Anforderung besteht.
    const parts: GraphNode[] = [];
    const partY = topHeight + 70;
    let partX = 0;

    (focus.resourceRefs ?? []).forEach((r, i) => {
        const node: GraphNode = {
            key: `res-${r.name}-${i}`, kind: 'resource', ref: r.name,
            title: `Ressource, Anzahl ${r.anzahl}${r.per ? ` je ${r.per}` : ''}`,
            badges: r.articleRefs?.length ? [`${r.articleRefs.length} Artikel`] : [],
            x: partX, y: partY, w: NODE_W, h: NODE_H,
        };
        parts.push(node);
        partX += NODE_W + 20;
    });

    (focus.articleRefs ?? []).forEach((a, i) => {
        const nr = a.aNr || a.gId || '?';
        const article = a.aNr ? articleByNr.get(a.aNr) : undefined;
        const noPrice = !!article && !article.vkf1;
        const node: GraphNode = {
            key: `art-${nr}-${i}`, kind: 'article', ref: nr,
            title: article?.beschreibung || (a.gId ? `Gruppe ${a.gId}` : 'unbekannter Artikel'),
            badges: article?.vkf1 ? [`${article.vkf1.toFixed(2)} €`] : [],
            warn: noPrice
                ? 'Kein Preis hinterlegt — steht im Angebot mit 0,00 €.'
                : (!article && a.aNr ? 'Artikel nicht im Katalog gefunden.' : undefined),
            x: partX, y: partY, w: NODE_W, h: NODE_H,
        };
        parts.push(node);
        partX += NODE_W + 20;
    });

    parts.forEach((p) => {
        nodes.push(p);
        edges.push({ key: `e-${p.key}`, from: focusNode, to: p, dashed: true });
    });

    const width = Math.max(colRight + NODE_W, partX) + 8;
    const height = (parts.length > 0 ? partY + NODE_H : topHeight) + 8;

    return { nodes, edges, groupLabels, width, height };
}

export default RequirementGraph;
